import { jsPDF } from "jspdf";
import { BRAND } from "./brand";
import { getForm } from "./formTemplates";

const GREEN = [27, 107, 76];
const DARK = [17, 24, 39];
const VALUE = [30, 41, 59];
const MUTED = [100, 116, 139];

async function urlToDataUrl(url) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
}

const yesNo = (v) => (v ? "Yes" : "No");

export async function generateReportPDF(ctx, { returnBlob = false } = {}) {
  const { report, customer, site, pump } = ctx;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  let y = 0;

  const logo = await urlToDataUrl(BRAND.logoMark);
  const formDef = getForm(report.form_type);

  const ensure = (need) => {
    if (y + need > H - 62) {
      footer();
      doc.addPage();
      y = M;
    }
  };

  const footer = () => {
    const fy = H - 54;
    doc.setDrawColor(210, 214, 220);
    doc.setLineWidth(0.5);
    doc.line(M, fy, W - M, fy);
    doc.setTextColor(...MUTED);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.text(`${BRAND.name}  •  Company Reg. ${BRAND.cro}  •  VAT ${BRAND.vat}`, M, fy + 12);
    doc.setFont("helvetica", "normal");
    doc.text(`${BRAND.address}  •  ${BRAND.phone}  •  ${BRAND.email}`, M, fy + 22);
    doc.text(`${BRAND.website}  •  ${BRAND.tagline}`, M, fy + 32);
    doc.text(`Page ${doc.internal.getNumberOfPages()}`, W - M, fy + 32, { align: "right" });
  };

  // ---- Header band ----
  doc.setFillColor(...GREEN);
  doc.rect(0, 0, W, 92, "F");
  if (logo) {
    try {
      doc.addImage(logo, "PNG", M, 18, 56, 56);
    } catch (e) {}
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(BRAND.name.toUpperCase(), M + 70, 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(BRAND.tagline, M + 70, 60);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(formDef.pdfTitle, W - M, 50, { align: "right" });
  y = 116;

  // ---- meta two columns ----
  const status = (report.status || "draft").toUpperCase();
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("STATUS", M, y);
  doc.text("SERVICE DATE", W / 2, y);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...DARK);
  doc.setFontSize(12);
  doc.text(status, M, y + 16);
  doc.text(report.service_date || "—", W / 2, y + 16);
  y += 38;

  const block = (title, rows) => {
    const visible = rows.filter(([, v]) => v != null && v !== "");
    if (!visible.length) return;
    ensure(30);
    doc.setFillColor(243, 246, 244);
    doc.rect(M, y, W - 2 * M, 22, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...GREEN);
    doc.text(title, M + 8, y + 15);
    y += 30;

    doc.setFontSize(9.5);
    const contentW = W - 2 * M;
    const labelW = contentW * 0.5;      // left column for the label
    const valX = M + 8 + labelW + 10;   // value column start
    const valW = contentW - labelW - 28;
    visible.forEach(([k, v]) => {
      doc.setFont("helvetica", "bold");
      const labelLines = doc.splitTextToSize(String(k), labelW - 8);
      doc.setFont("helvetica", "normal");
      const valLines = doc.splitTextToSize(String(v), valW);
      const rowH = Math.max(labelLines.length, valLines.length) * 12 + 5;
      ensure(rowH);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...DARK);
      doc.text(labelLines, M + 8, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...VALUE);
      doc.text(valLines, valX, y);
      y += rowH;
    });
    y += 8;
  };

  block("Customer", [
    ["Customer", customer?.name],
    ["Contact", customer?.contact_person],
    ["Email", customer?.email],
    ["Phone", customer?.phone],
    ["Billing address", customer?.billing_address],
  ]);
  block("Site", [
    ["Site", site?.name],
    ["Address", site?.address],
    ["Engineer", report.engineer_name],
  ]);
  block("Heat Pump", [
    ["Manufacturer", pump?.manufacturer],
    ["Model", pump?.model],
    ["Serial number", pump?.serial_number],
    ["Indoor unit model", pump?.indoor_unit],
    ["Indoor unit serial", pump?.indoor_serial_number],
    ["Outdoor unit model", pump?.outdoor_unit],
    ["Refrigerant", pump?.refrigerant_type],
    ["Installed", pump?.installation_date],
    ["Warranty", pump?.warranty_details],
  ]);

  // ---- checklist sections ----
  const data = report.data || {};
  formDef.sections.forEach((sec) => {
    const rows = [];
    sec.fields.forEach((f) => {
      const val = data[f.id];
      if (f.type === "check") rows.push([f.label, yesNo(val)]);
      else if (f.type === "remarks") {
        if (val) rows.push([f.label, val]);
      } else if (val !== undefined && val !== "" && val !== null) {
        rows.push([f.label + (f.unit ? ` (${f.unit})` : ""), val]);
      }
    });
    if (rows.length) block(sec.title, rows);
  });

  block("Outcome", [
    ["Final status", report.final_status],
    ["Engineer notes", data.engineer_notes],
    ["Follow-up actions", report.follow_up_actions],
    ["Parts required", report.parts_required],
  ]);

  // ---- photos ----
  const photos = report.photos || [];
  if (photos.length) {
    ensure(40);
    doc.setFillColor(243, 246, 244);
    doc.rect(M, y, W - 2 * M, 22, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...GREEN);
    doc.text("Photos", M + 8, y + 15);
    y += 32;
    const pw = (W - 2 * M - 20) / 3;
    let x = M;
    photos.slice(0, 9).forEach((p, i) => {
      ensure(pw + 10);
      if (i % 3 === 0 && i !== 0) {
        y += pw + 10;
        x = M;
      }
      try {
        doc.addImage(p.dataUrl, "JPEG", x, y, pw, pw, undefined, "FAST");
      } catch (e) {}
      x += pw + 10;
    });
    y += pw + 16;
  }

  // ---- signatures ----
  ensure(120);
  doc.setFillColor(243, 246, 244);
  doc.rect(M, y, W - 2 * M, 22, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...GREEN);
  doc.text("Sign-off", M + 8, y + 15);
  y += 34;
  const colW = (W - 2 * M - 20) / 2;
  const sig = (label, img, x) => {
    doc.setDrawColor(...MUTED);
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    if (img) {
      try {
        doc.addImage(img, "PNG", x, y, colW, 60);
      } catch (e) {}
    }
    doc.line(x, y + 64, x + colW, y + 64);
    doc.text(label, x, y + 78);
  };
  sig(
    `Engineer: ${report.engineer_name || ""}`,
    report.engineer_signature,
    M
  );
  sig("Customer signature", report.customer_signature, M + colW + 20);
  y += 92;
  footer();

  if (returnBlob) return doc.output("blob");
  const fname = `Sterio_Service_${(customer?.name || "report").replace(
    /\s+/g,
    "_"
  )}_${report.service_date || ""}.pdf`;
  doc.save(fname);
}
